import React, { useEffect } from "react";
import { PortableText } from "@portabletext/react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import CustomLink from "../common/Link";
import YouTubeEmbed from "./YoutubeEmbed";
import Image from "gatsby-plugin-sanity-image";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
  EmailIcon,
} from "react-share";

const Content = ({ data, cta, prev, next }) => {
  // Reading progress functionality
  // useEffect(() => {
  //   const updateReadingProgress = () => {
  //     const article = document.querySelector("article");
  //     const progressBar = document.getElementById("reading-progress");

  //     if (article && progressBar) {
  //       const articleRect = article.getBoundingClientRect();
  //       const articleHeight = article.offsetHeight;
  //       const windowHeight = window.innerHeight;
  //       const scrollTop =
  //         window.pageYOffset || document.documentElement.scrollTop;

  //       const articleTop = article.offsetTop;
  //       const readableHeight = articleHeight - windowHeight;
  //       const progress = Math.max(
  //         0,
  //         Math.min(100, ((scrollTop - articleTop) / readableHeight) * 100)
  //       );

  //       progressBar.style.width = `${progress}%`;
  //     }
  //   };

  //   window.addEventListener("scroll", updateReadingProgress);
  //   return () => window.removeEventListener("scroll", updateReadingProgress);
  // }, []);

  // YouTube component

  // Image component
  const ImageComponent = ({ value }) => {
    const gatsbyImage = value?.asset?.gatsbyImageData || getImage(value?.asset);

    if (gatsbyImage) {
      return (
        <figure className="my-8">
          <div className="rounded-lg overflow-hidden shadow-md">
            <GatsbyImage
              image={gatsbyImage}
              alt={value?.alt || "Blog image"}
              className="w-full"
            />
          </div>
          {value?.caption && (
            <figcaption className="mt-3 text-sm text-gray-600 italic text-center">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    }

    if (value?.asset?._ref) {
      return (
        <figure className="my-8">
          <div className="rounded-lg overflow-hidden shadow-md">
            <Image
              asset={value.asset}
              crop={value.crop}
              hotspot={value.hotspot}
              alt={value?.alt || "Blog image"}
              className="w-full"
            />
          </div>
          {value?.caption && (
            <figcaption className="mt-3 text-sm text-gray-600 italic text-center">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    }

    return null;
  };

  // Table component
  const TableComponent = ({ value }) => {
    if (!value.rows) return null;

    return (
      <div className="my-8 overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <tbody>
            {value.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
              >
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-6 py-4 border-b border-gray-200 text-gray-900"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // PortableText components
  const components = {
    types: {
      image: ImageComponent,
      youtube: YouTubeEmbed,
      table: TableComponent,
    },
    block: {
      normal: ({ children }) => (
        <p className="mb-6 text-gray-800 leading-relaxed text-lg">{children}</p>
      ),
      h1: ({ children }) => (
        <h2 className="text-3xl md:text-4xl font-bold text-black mt-12 mb-6 leading-tight">
          {children}
        </h2>
      ),
      h2: ({ children }) => (
        <h2 className="text-2xl md:text-3xl font-bold text-black mt-10 mb-5 leading-tight">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-xl md:text-2xl font-bold text-black mt-8 mb-4 leading-tight">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-lg md:text-xl font-bold text-black mt-6 mb-3 leading-tight">
          {children}
        </h4>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-logo1 pl-6 py-4 my-8 bg-gray-50 rounded-r-lg">
          <div className="text-lg text-gray-700 italic font-medium leading-relaxed">
            {children}
          </div>
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <ul className="list-disc list-inside mb-6 space-y-2 text-gray-800 text-lg ml-4">
          {children}
        </ul>
      ),
      number: ({ children }) => (
        <ol className="list-decimal list-inside mb-6 space-y-2 text-gray-800 text-lg ml-4">
          {children}
        </ol>
      ),
    },
    listItem: {
      bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
      number: ({ children }) => <li className="leading-relaxed">{children}</li>,
    },
    marks: {
      strong: ({ children }) => (
        <strong className="font-bold text-black">{children}</strong>
      ),
      em: ({ children }) => <em className="italic">{children}</em>,
      code: ({ children }) => (
        <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono">
          {children}
        </code>
      ),
      link: ({ children, value }) => (
        <a
          href={value.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-logo1 hover:text-opacity-80 underline transition-colors duration-200"
        >
          {children}
        </a>
      ),
    },
  };

  return (
    <main className="bg-white">
      {/* Article Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            {data._rawDescription && (
              <PortableText
                value={data._rawDescription}
                components={components}
              />
            )}
          </div>
          {/* Navigation to other posts */}
          <section className="bg-white border-t border-gray-200 py-8">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      {prev?.slug?.current && (
                        <CustomLink
                          cls="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 font-medium shadow-sm transition-colors duration-200"
                          data={{
                            linkType: "slug",
                            buttonText: "Previous",
                            slug: {
                              current: `/blog/${prev.slug.current}`,
                            },
                          }}
                        >
                          <svg
                            className="mr-1 w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Previous
                        </CustomLink>
                      )}
                    </div>
                    <div className="flex-1 text-right">
                      {next?.slug?.current && (
                        <CustomLink
                          cls="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 font-medium shadow-sm transition-colors duration-200"
                          data={{
                            linkType: "slug",
                            buttonText: "Next",
                            slug: {
                              current: `/blog/${next.slug.current}`,
                            },
                          }}
                        >
                          Next
                          <svg
                            className="ml-1 w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </CustomLink>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <CustomLink
                      cls="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-200 font-medium transition-colors duration-200"
                      data={{
                        linkType: "slug",
                        buttonText: "Back to all articles",
                        slug: {
                          current: "/blog",
                        },
                      }}
                    >
                      <svg
                        className="mr-1 w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Back to all articles
                    </CustomLink>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Social Share */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">
                Share this article:
              </span>
              <div className="flex gap-4">
                {/* Native Share */}
                <button
                  onClick={() => {
                    if (typeof window === "undefined") return;
                    const url = window?.location?.href;
                    const text = `Check out this article: ${data.blogTitle}`;
                    if (navigator.share) {
                      navigator.share({ title: data.blogTitle, text, url });
                    } else {
                      navigator.clipboard.writeText(`${text} ${url}`);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                  </svg>
                  Share
                </button>

                {/* Facebook Share */}
                <FacebookShareButton
                  url={
                    typeof window !== "undefined" ? window?.location?.href : ""
                  }
                  quote={data.blogTitle}
                  hashtag="#blog"
                  className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors duration-200"
                >
                  <FacebookIcon size={30} round={false} />
                  <span className="text-black">Facebook</span>
                </FacebookShareButton>

                {/* Twitter Share */}
                <TwitterShareButton
                  url={
                    typeof window !== "undefined" ? window?.location?.href : ""
                  }
                  title={data.blogTitle}
                  hashtags={["blog", "article"]}
                  className="flex items-center gap-2 px-4 py text-black rounded-lg transition-colors duration-200"
                >
                  <TwitterIcon size={30} round={false} />
                  <span className="text-black">Twitter</span>
                </TwitterShareButton>

                {/* LinkedIn Share */}
                <LinkedinShareButton
                  url={
                    typeof window !== "undefined" ? window?.location?.href : ""
                  }
                  title={data.blogTitle}
                  summary={data.blogTitle}
                  source="Blog"
                  className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors duration-200"
                >
                  <LinkedinIcon size={30} round={false} />
                  <span className="text-black">LinkedIn</span>
                </LinkedinShareButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      {cta?.cta?.enable && (
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                {cta.cta.title}
              </h2>
              {cta.cta.description && (
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  {cta.cta.description}
                </p>
              )}
              {cta.cta.buttonText && cta.cta.buttonSlug?.current && (
                <CustomLink
                  cls="inline-flex items-center justify-center px-8 py-3 text-sm font-medium uppercase tracking-wider text-black bg-logo1 rounded-lg hover:bg-[#EFEFEF] hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                  data={{
                    linkType: "slug",
                    buttonText: cta.cta.buttonText,
                    slug: cta.cta.buttonSlug,
                  }}
                >
                  <span>{cta.cta.buttonText}</span>
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </CustomLink>
              )}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Content;
